import { useEffect, useMemo, useState } from 'react';
import { hasPermission, PERMISSIONS } from '../state/ops';
import { createLoan, directoryLookup, listClients, listLoans, fetchConfig } from '../api';
import { showError, showSuccess, showWarning } from '../components/Toaster';
import Pager from '../components/Pager';
import { IconSearch, IconPlus } from '../components/Icons';

export default function Loans() {
  const canView = hasPermission(PERMISSIONS.LOANS_VIEW);
  const canCreate = hasPermission(PERMISSIONS.LOANS_CREATE);
  const [form, setForm] = useState({
    accountNumber: '',
    principal: '',
    interestRate: '',
    durationMonths: '',
    startDate: '',
    g1Name: '',
    g1Phone: '',
    g1Id: '',
    g2Name: '',
    g2Phone: '',
    g2Id: '',
    collateralDesc: '',
    collateralValue: '',
    collateralDoc: null
  });
  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const changeFile = (e) => setForm({ ...form, collateralDoc: e.target.files?.[0] || null });
  const [client, setClient] = useState(null);
  const [cfg, setCfg] = useState({ defaultLoanRate: 0, serviceFeeRate: 0, adminFeeRate: 0, commitmentFeeRate: 0 });
  const lookup = () => {
    const q = (form.accountNumber || '').trim();
    if (!q) return;
    if (/^\d{10}$/.test(q)) {
      directoryLookup(q).then(setClient).catch((e) => { setClient(null); if (e && e.status === 404) showError('Account not found'); else showError('Lookup failed'); });
    } else {
      listClients({ q }).then(list => {
        if (list && list.length) {
          setForm(f => ({ ...f, accountNumber: list[0].accountNumber }));
          directoryLookup(list[0].accountNumber).then(setClient).catch((e) => { setClient(null); if (e && e.status === 404) showError('Account not found'); else showError('Lookup failed'); });
        }
        else showWarning('No matching client found');
      }).catch(() => { showError('Lookup failed'); });
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (!canCreate) {
        showError('Not authorized to create loans');
        return;
      }
      await createLoan({
        accountNumber: form.accountNumber,
        principal: Number(form.principal),
        rate: Number(form.interestRate),
        termMonths: Number(form.durationMonths),
        startDate: form.startDate,
        guarantor1: { name: form.g1Name, phone: form.g1Phone, id: form.g1Id },
        guarantor2: { name: form.g2Name, phone: form.g2Phone, id: form.g2Id },
        collateral: { description: form.collateralDesc, value: Number(form.collateralValue || 0) },
      });
      showSuccess('Loan created and submitted for approval');
      setForm({ accountNumber: '', principal: '', interestRate: '', durationMonths: '', startDate: '', g1Name: '', g1Phone: '', g1Id: '', g2Name: '', g2Phone: '', g2Id: '', collateralDesc: '', collateralValue: '', collateralDoc: null });
    } catch (e) {
      if (e && e.message && e.message.includes('duplicate')) showError('Duplicate contact detected');
      else showError('Failed to create loan');
    }
  };
  const [rows, setRows] = useState([]);
  const [loanIdFilter, setLoanIdFilter] = useState('');
  useEffect(() => {
    if (!canView) return;
    listLoans({}).then(setRows).catch(() => setRows([]));
  }, [canView]);
  useEffect(() => {
    if (!canView) return;
    fetchConfig().then(cfg => {
      setCfg({
        defaultLoanRate: Number(cfg.defaultLoanRate ?? 0),
        serviceFeeRate: Number(cfg.serviceFeeRate ?? 0),
        adminFeeRate: Number(cfg.adminFeeRate ?? 0),
        commitmentFeeRate: Number(cfg.commitmentFeeRate ?? 0),
      });
      setForm(f => ({ ...f, interestRate: f.interestRate || String(Number(cfg.defaultLoanRate ?? 0)) }));
    }).catch(() => {});
  }, [canView]);
  const interestCalc = useMemo(() => {
    const p = Number(form.principal || 0);
    const r = Number(form.interestRate || 0) / 100;
    const m = Number(form.durationMonths || 0) / 12;
    const ti = p * r * m;
    const sFee = p * (Number(cfg.serviceFeeRate || 0) / 100);
    const aFee = p * (Number(cfg.adminFeeRate || 0) / 100);
    const cFee = p * (Number(cfg.commitmentFeeRate || 0) / 100);
    const fees = sFee + aFee + cFee;
    return {
      serviceFee: Math.round(sFee * 100) / 100,
      adminFee: Math.round(aFee * 100) / 100,
      commitmentFee: Math.round(cFee * 100) / 100,
      feesTotal: Math.round(fees * 100) / 100,
      totalInterest: Math.round(ti * 100) / 100,
      totalDue: Math.round((p + ti + fees) * 100) / 100
    };
  }, [form.principal, form.interestRate, form.durationMonths, cfg.serviceFeeRate, cfg.adminFeeRate, cfg.commitmentFeeRate]);
  const withComputed = useMemo(() => {
    const plusMonths = (dateStr, m) => {
      try {
        const d = new Date(dateStr);
        d.setMonth(d.getMonth() + (m || 0));
        return d.toISOString();
      } catch { return ''; }
    };
    const daysDiff = (d) => {
      try {
        const dd = new Date(d);
        const now = new Date();
        return Math.ceil((dd.getTime() - now.getTime()) / (24 * 3600 * 1000));
      } catch { return null; }
    };
    return rows.map(r => {
      const start = r.approvedAt || r.createdAt || '';
      const due = start ? plusMonths(start, r.termMonths) : '';
      const daysToDue = due ? daysDiff(due) : null;
      const overdueDays = daysToDue != null ? Math.max(0, -daysToDue) : 0;
      return { ...r, _due: due, _daysToDue: daysToDue, _overdueDays: overdueDays };
    });
  }, [rows]);
  const listFiltered = withComputed.filter(r => !loanIdFilter || String(r.id || '').includes(loanIdFilter.trim()));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const start = (page - 1) * pageSize;
  const pageRows = listFiltered.slice(start, start + pageSize);
  if (!canView) return <div className="card">Not authorized.</div>;
  return (
    <div className="stack">
      <h1>Loans</h1>
      {canCreate && <section className="card">
        <h3>New Loan</h3>
        <form onSubmit={submit} className="form" style={{ maxWidth: 520 }}>
          <label>
            Client Account Number
            <div className="row">
              <input className="input" name="accountNumber" value={form.accountNumber} onChange={change} placeholder="Account / Name / ID" required />
              <button className="btn" type="button" onClick={lookup}><IconSearch /><span>Lookup</span></button>
            </div>
          </label>
          {client && (
            <div className="row" style={{ gap: 24 }}>
              <div><div style={{ color: '#64748b', fontSize: 12 }}>Name</div><div>{client.name}</div></div>
              <div><div style={{ color: '#64748b', fontSize: 12 }}>National ID</div><div>{client.nationalId}</div></div>
              <div><div style={{ color: '#64748b', fontSize: 12 }}>DOB</div><div>{client.dob}</div></div>
              <div><div style={{ color: '#64748b', fontSize: 12 }}>Phone</div><div>{client.phone}</div></div>
            </div>
          )}
          <label>
            Principal Amount
            <input className="input" type="number" name="principal" value={form.principal} onChange={change} required />
          </label>
          <label>
            Interest Rate (%)
            <input className="input" type="number" name="interestRate" value={form.interestRate} onChange={change} required />
          </label>
          <label>
            Duration (months)
            <input className="input" type="number" name="durationMonths" value={form.durationMonths} onChange={change} required />
          </label>
          <div className="row" style={{ gap: 24 }}>
            <div><div style={{ fontSize: 12, color: '#64748b' }}>Total Interest</div><div style={{ fontWeight: 600 }}>{interestCalc.totalInterest.toLocaleString('en-GH', { style: 'currency', currency: 'GHS' })}</div></div>
            <div><div style={{ fontSize: 12, color: '#64748b' }}>Fees</div><div style={{ fontWeight: 600 }}>{interestCalc.feesTotal.toLocaleString('en-GH', { style: 'currency', currency: 'GHS' })}</div></div>
            <div><div style={{ fontSize: 12, color: '#64748b' }}>Total Payable</div><div style={{ fontWeight: 600 }}>{interestCalc.totalDue.toLocaleString('en-GH', { style: 'currency', currency: 'GHS' })}</div></div>
          </div>
          <div className="row" style={{ gap: 24 }}>
            <div><div style={{ fontSize: 12, color: '#64748b' }}>Service Fee</div><div>{(interestCalc.serviceFee).toLocaleString('en-GH', { style: 'currency', currency: 'GHS' })}</div></div>
            <div><div style={{ fontSize: 12, color: '#64748b' }}>Administration Fee</div><div>{(interestCalc.adminFee).toLocaleString('en-GH', { style: 'currency', currency: 'GHS' })}</div></div>
            <div><div style={{ fontSize: 12, color: '#64748b' }}>Commitment Fee</div><div>{(interestCalc.commitmentFee).toLocaleString('en-GH', { style: 'currency', currency: 'GHS' })}</div></div>
          </div>
          <label>
            Start Date
            <input className="input" type="date" name="startDate" value={form.startDate} onChange={change} required />
          </label>
          <div className="stack">
            <h3>Guarantor 1</h3>
            <label>
              Name
              <input className="input" name="g1Name" value={form.g1Name} onChange={change} required />
            </label>
            <label>
              Phone
              <input className="input" name="g1Phone" value={form.g1Phone} onChange={change} required />
            </label>
            <label>
              National ID / Passport
              <input className="input" name="g1Id" value={form.g1Id} onChange={change} required />
            </label>
          </div>
          <div className="stack">
            <h3>Guarantor 2</h3>
            <label>
              Name
              <input className="input" name="g2Name" value={form.g2Name} onChange={change} required />
            </label>
            <label>
              Phone
              <input className="input" name="g2Phone" value={form.g2Phone} onChange={change} required />
            </label>
            <label>
              National ID / Passport
              <input className="input" name="g2Id" value={form.g2Id} onChange={change} required />
            </label>
          </div>
          <div className="stack">
            <h3>Collateral</h3>
            <label>
              Description
              <input className="input" name="collateralDesc" value={form.collateralDesc} onChange={change} />
            </label>
            <label>
              Estimated Value
              <input className="input" type="number" name="collateralValue" value={form.collateralValue} onChange={change} />
            </label>
            <label>
              Supporting Document (photo or PDF)
              <input className="input" type="file" accept="image/*,application/pdf" onChange={changeFile} />
            </label>
          </div>
          <button className="btn btn-primary" type="submit"><IconPlus /><span>Create Loan</span></button>
        </form>
      </section>}
      <section className="card">
        <h3>Loans List</h3>
        <div style={{ marginBottom: 12, maxWidth: 320 }}>
          <label>
            Loan ID
            <input className="input" placeholder="e.g. L0000123" value={loanIdFilter} onChange={(e) => setLoanIdFilter(e.target.value)} />
          </label>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Loan ID</th>
              <th>Account</th>
              <th>Principal</th>
              <th>Total Interest</th>
              <th>Total Due</th>
              <th>Due</th>
              <th>Days To Due</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map(r => (
              <tr key={r.id} style={{ background: (r._overdueDays || 0) > 0 ? '#fff1f2' : undefined }}>
                <td>{r.id}</td>
                <td>{r.accountNumber}</td>
                <td>{Number(r.principal || 0).toLocaleString('en-GH', { style: 'currency', currency: 'GHS' })}</td>
                <td>{Number(r.totalInterest || 0).toLocaleString('en-GH', { style: 'currency', currency: 'GHS' })}</td>
                <td>{Number(r.totalDue || 0).toLocaleString('en-GH', { style: 'currency', currency: 'GHS' })}</td>
                <td>{r._due ? r._due.slice(0,10) : '—'}</td>
                <td>{r._daysToDue != null ? String(r._daysToDue) : '—'}</td>
                <td>{r.status}</td>
              </tr>
            ))}
            {!listFiltered.length && <tr><td colSpan="6">No loans loaded.</td></tr>}
          </tbody>
        </table>
        <Pager total={listFiltered.length} page={page} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(n) => { setPageSize(n); setPage(1); }} />
      </section>
    </div>
  );
}
